import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterControls = ({ 
  filters, 
  onFiltersChange,
  onReset,
  onSavePreset 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Mock data for filter options
  const filterOptions = {
    countries: [
      { code: 'DE', name: 'Germany', flag: '🇩🇪' },
      { code: 'FR', name: 'France', flag: '🇫🇷' },
      { code: 'IT', name: 'Italy', flag: '🇮🇹' },
      { code: 'ES', name: 'Spain', flag: '🇪🇸' },
      { code: 'PL', name: 'Poland', flag: '🇵🇱' },
      { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
      { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
      { code: 'AT', name: 'Austria', flag: '🇦🇹' },
      { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
      { code: 'HU', name: 'Hungary', flag: '🇭🇺' }
    ],
    themes: [
      { id: 'economic', label: 'Economic Manipulation', count: 24 },
      { id: 'social', label: 'Social Division', count: 18 },
      { id: 'political', label: 'Political Undermining', count: 32 },
      { id: 'health', label: 'Health Misinformation', count: 15 },
      { id: 'environmental', label: 'Climate Denial', count: 12 },
      { id: 'security', label: 'Security Threats', count: 21 }
    ],
    sources: [
      { id: 'social_media', label: 'Social Media', count: 156 },
      { id: 'news_sites', label: 'News Websites', count: 89 },
      { id: 'messaging', label: 'Messaging Apps', count: 67 },
      { id: 'forums', label: 'Forums & Boards', count: 43 },
      { id: 'video_platforms', label: 'Video Platforms', count: 78 }
    ]
  };

  const savedPresets = [
    { id: 'high_risk', name: 'High Risk Narratives', icon: 'AlertTriangle' },
    { id: 'cross_border', name: 'Cross-Border Campaigns', icon: 'Globe' },
    { id: 'recent_activity', name: 'Recent Activity', icon: 'Clock' },
    { id: 'coordinated', name: 'Coordinated Inauthentic', icon: 'GitBranch' }
  ];

  const handleFilterChange = (category, value, checked) => {
    const currentValues = filters?.[category] || [];
    const newValues = checked 
      ? [...currentValues, value]
      : currentValues?.filter(v => v !== value);
    
    onFiltersChange({
      ...filters,
      [category]: newValues
    });
  };

  const handleDateRangeChange = (field, value) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters?.dateRange,
        [field]: value
      }
    });
  };

  const handleThresholdChange = (field, value) => {
    onFiltersChange({
      ...filters,
      thresholds: {
        ...filters?.thresholds,
        [field]: parseInt(value)
      }
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters?.countries?.length) count += filters?.countries?.length;
    if (filters?.themes?.length) count += filters?.themes?.length;
    if (filters?.sources?.length) count += filters?.sources?.length;
    return count;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-foreground">Filters</h3>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
              {getActiveFiltersCount()} active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={onReset}
          >
            Reset
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      </div>
      {/* Quick Presets */}
      <div className="p-4 border-b border-border bg-muted/30">
        <h4 className="text-sm font-semibold text-foreground mb-3">Quick Presets</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {savedPresets?.map((preset) => (
            <Button
              key={preset?.id}
              variant="outline"
              size="sm"
              iconName={preset?.icon}
              iconPosition="left"
              onClick={() => {
                // Load preset logic
              }}
              className="justify-start"
            >
              {preset?.name}
            </Button>
          ))}
        </div>
      </div>
      {/* Date Range & Thresholds */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Date Range */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Date Range</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="From"
                type="date"
                value={filters?.dateRange?.from || '2024-09-01'}
                onChange={(e) => handleDateRangeChange('from', e?.target?.value)}
              />
              <Input
                label="To"
                type="date"
                value={filters?.dateRange?.to || '2024-09-21'}
                onChange={(e) => handleDateRangeChange('to', e?.target?.value)}
              />
            </div>
          </div>

          {/* Thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Thresholds</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-muted-foreground">Confidence Score</label>
                <div className="flex items-center space-x-3 mt-1">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters?.thresholds?.confidence || 70}
                    onChange={(e) => handleThresholdChange('confidence', e?.target?.value)}
                    className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-sm font-medium text-foreground w-8">
                    {filters?.thresholds?.confidence || 70}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Virality Score</label>
                <div className="flex items-center space-x-3 mt-1">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters?.thresholds?.virality || 50}
                    onChange={(e) => handleThresholdChange('virality', e?.target?.value)}
                    className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-sm font-medium text-foreground w-8">
                    {filters?.thresholds?.virality || 50}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Expandable Filters */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Countries */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Countries</h4>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {filterOptions?.countries?.map((country) => (
                <Checkbox
                  key={country?.code}
                  label={
                    <div className="flex items-center space-x-2">
                      <span>{country?.flag}</span>
                      <span>{country?.name}</span>
                    </div>
                  }
                  checked={filters?.countries?.includes(country?.code) || false}
                  onChange={(e) => handleFilterChange('countries', country?.code, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Narrative Themes */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Narrative Themes</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {filterOptions?.themes?.map((theme) => (
                <Checkbox
                  key={theme?.id}
                  label={
                    <div className="flex items-center justify-between w-full">
                      <span>{theme?.label}</span>
                      <span className="text-xs text-muted-foreground">({theme?.count})</span>
                    </div>
                  }
                  checked={filters?.themes?.includes(theme?.id) || false}
                  onChange={(e) => handleFilterChange('themes', theme?.id, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Source Types */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Source Types</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {filterOptions?.sources?.map((source) => (
                <Checkbox
                  key={source?.id}
                  label={
                    <div className="flex items-center justify-between w-full">
                      <span>{source?.label}</span>
                      <span className="text-xs text-muted-foreground">({source?.count})</span>
                    </div>
                  }
                  checked={filters?.sources?.includes(source?.id) || false}
                  onChange={(e) => handleFilterChange('sources', source?.id, e?.target?.checked)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Save Preset */}
      {getActiveFiltersCount() > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-3">
            <Input
              placeholder="Preset name..."
              value={presetName}
              onChange={(e) => setPresetName(e?.target?.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Save"
              iconPosition="left"
              onClick={() => {
                if (presetName?.trim()) {
                  onSavePreset(presetName?.trim(), filters);
                  setPresetName('');
                }
              }}
              disabled={!presetName?.trim()}
            >
              Save Preset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;